import React from 'react';
import {
  Box,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer, TableFooter,
  TablePagination,
  TableRow,
} from '@mui/material';
import dayjs from 'dayjs';
import { useQuery } from '@apollo/client';
import EnhancedTableHeader from '../EnhancedTableHeader';
import TablePaginationActions from '../TablePaginationActions';
import { GET_AWO_REPORTS } from '../../queries';
import reportLabel from './reportLabel';

interface AwoReport {
  id: string;
  dateCreated: string;
  documentTitle: string | null;
  documentId: string;
  pdfMonkeyUrl: {
    url: string;
  };
}

interface AwoReportsCount {
  count: number;
}

const headCells = [{
  id: 'date_created',
  label: 'Date',
  width: '50%',
}, {
  id: 'title',
  label: 'Title',
  width: '50%',
  disableSorting: true,
}] as const;

const ROWS_PER_PAGE = 12;

function ReportsPanel() {
  const [filters, setFilters] = React.useState({
    limit: ROWS_PER_PAGE,
    offset: 0,
    order: 'desc',
    orderBy: 'date_created',
  });
  const { loading, data } = useQuery<{ awoReports: AwoReport[], awoReportsCount: AwoReportsCount }>(GET_AWO_REPORTS, {
    variables: filters,
  });

  const handleChangeOrder = (_: never, field: 'date_created') => {
    const isAsc = filters.orderBy === field && filters.order === 'asc';
    setFilters((prevState) => ({
      ...prevState,
      order: isAsc ? 'desc' : 'asc',
      orderBy: field,
    }));
  };

  const handleChangeRowsPerPage = (event: any) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setFilters((prevState) => ({
      ...prevState,
      limit: newRowsPerPage,
      offset: 0,
    }));
  };

  const handleChangePage = (_: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, page: number) => {
    setFilters((prevState) => ({
      ...prevState,
      offset: ROWS_PER_PAGE * page,
    }));
  };

  return (
    <Grid container direction="row" alignItems="center" justifyContent="center">
      <Grid item sx={{ flexGrow: 1, maxWidth: 768 }}>
        <Box sx={{ mx: -2 }}>
          <TableContainer>
            <Table aria-label="Reports Table">
              <EnhancedTableHeader
                headCells={headCells}
                order={filters.order}
                orderBy={filters.orderBy}
                handleChangeOrder={handleChangeOrder}
              />
              <TableBody>
                {loading ? (
                  <tr>
                    <td aria-label="Loading progress" colSpan={10}>
                      <Box sx={{ height: '4px', transform: 'translateY(-1px)' }}>
                        <LinearProgress />
                      </Box>
                    </td>
                  </tr>
                ) : null}
                {
                  !loading && data?.awoReports.map((report) => (
                    <TableRow
                      key={report.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => window.location.assign(report.pdfMonkeyUrl.url)}
                    >
                      <TableCell>{dayjs(report.dateCreated).format('M/D/YY')}</TableCell>
                      <TableCell>{reportLabel(report)}</TableCell>
                    </TableRow>
                  ))
                }
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    colSpan={8}
                    rowsPerPageOptions={[ROWS_PER_PAGE]}
                    count={data?.awoReportsCount?.count || 0}
                    rowsPerPage={ROWS_PER_PAGE}
                    page={filters.offset / ROWS_PER_PAGE}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Box>
      </Grid>
    </Grid>
  );
}

export default ReportsPanel;
